if (!customElements.get('td-product-breakdown')) {
  class TdProductBreakdown extends HTMLElement {
    static CONFIG = {
      desktopMediaQuery: '(min-width: 990px)',
      offsetLimit: 100,
      lineWidthPctDefault: 80,
      lineHeightPctDefault: 40,
      selectors: {
        items: '[data-td-pb-items]',
        item: '[data-td-pb-item]',
        media: '[data-td-pb-media]',
        itemAnchor: '[data-td-pb-item-anchor-el]',
      },
    };

    handleLayoutChange = () => {
      this.queueUpdate();
    };

    connectedCallback() {
      this.teardown();

      this.controller = new AbortController();
      const { signal } = this.controller;
      const { desktopMediaQuery, selectors } = TdProductBreakdown.CONFIG;

      this.desktopMq = window.matchMedia(desktopMediaQuery);
      this.itemsContainer = this.querySelector(selectors.items);
      this.media = this.querySelector(selectors.media);

      if (this.desktopMq.addEventListener) {
        this.desktopMq.addEventListener('change', this.handleLayoutChange, { signal });
      } else if (this.desktopMq.addListener) {
        this.desktopMq.addListener(this.handleLayoutChange);
        this.removeMqListener = () => this.desktopMq?.removeListener(this.handleLayoutChange);
      }

      window.addEventListener('resize', this.handleLayoutChange, { passive: true, signal });
      document.addEventListener('shopify:block:select', this.handleLayoutChange, { signal });
      document.addEventListener('shopify:block:deselect', this.handleLayoutChange, { signal });

      this.queueUpdate();
    }

    disconnectedCallback() {
      this.teardown();
    }

    queueUpdate() {
      if (this.raf) cancelAnimationFrame(this.raf);

      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        if (!this.isConnected) return;
        this.updateLayout();
      });
    }

    updateLayout() {
      const { lineWidthPctDefault, lineHeightPctDefault, offsetLimit, selectors } = TdProductBreakdown.CONFIG;
      const items = this.getItems();
      if (!items.length || !this.itemsContainer || !this.media || !this.desktopMq?.matches) {
        this.clearDesktopStyles(items);
        return;
      }

      const mediaRect = this.media.getBoundingClientRect();
      const containerRect = this.itemsContainer.getBoundingClientRect();

      items.forEach((item) => {
        this.resetItemInlineStyles(item);

        const anchor = item.querySelector(selectors.itemAnchor);
        if (!(anchor instanceof HTMLElement)) return;

        const anchorRect = anchor.getBoundingClientRect();
        const itemPosition = item.dataset.tdPbItemPosition === 'right' ? 'right' : 'left';
        const lineWidthPct = this.parsePct(item.dataset.tdPbItemLineWidthPct, lineWidthPctDefault);
        const lineHeightPct = this.parsePct(item.dataset.tdPbItemLineHeightPct, lineHeightPctDefault);

        const horizontalDistancePx =
          itemPosition === 'left'
            ? Math.max(0, mediaRect.right - anchorRect.right)
            : Math.max(0, anchorRect.left - mediaRect.left);

        const lineWidthPx = horizontalDistancePx * (lineWidthPct / 100);
        item.style.setProperty('--td-product-breakdown-item-line-width', `${lineWidthPx.toFixed(2)}px`);

        const targetY = this.getAnchorTargetY(mediaRect, item.dataset.tdPbItemAnchor);
        const anchorCenterY = anchorRect.top + anchorRect.height / 2;
        const lineDirection = item.dataset.tdPbItemLineDirection === 'up' ? 'up' : 'down';
        const verticalDistancePx = this.getVerticalDistancePx(mediaRect, targetY, anchorCenterY, lineDirection);
        const lineHeightPx = verticalDistancePx * (lineHeightPct / 100);

        item.style.setProperty('--td-product-breakdown-item-line-height', `${lineHeightPx.toFixed(2)}px`);

        const rawOffset = Number(item.dataset.tdPbItemVerticalOffset || 0);
        const normalized = this.clamp(rawOffset, -offsetLimit, offsetLimit) / offsetLimit;
        if (!normalized) return;

        const itemRect = item.getBoundingClientRect();
        const topSpace = Math.max(0, itemRect.top - containerRect.top);
        const bottomSpace = Math.max(0, containerRect.height - (itemRect.top - containerRect.top + itemRect.height));
        const shift = normalized < 0 ? normalized * topSpace : normalized * bottomSpace;

        item.style.setProperty('--td-product-breakdown-item-shift', `${shift.toFixed(2)}px`);
      });
    }

    getItems() {
      const { selectors } = TdProductBreakdown.CONFIG;
      if (!this.itemsContainer) return [];
      return Array.from(this.itemsContainer.querySelectorAll(selectors.item));
    }

    getAnchorTargetY(mediaRect, anchor) {
      switch ((anchor || 'left_top').trim()) {
        case 'border_center':
          return mediaRect.top + mediaRect.height / 2;
        case 'border_bottom':
          return mediaRect.bottom;
        default:
          return mediaRect.top;
      }
    }

    getVerticalDistancePx(mediaRect, targetY, anchorCenterY, lineDirection) {
      const directionDistance = lineDirection === 'up' ? anchorCenterY - targetY : targetY - anchorCenterY;
      if (directionDistance > 0) return directionDistance;

      return lineDirection === 'up'
        ? Math.max(0, anchorCenterY - mediaRect.top)
        : Math.max(0, mediaRect.bottom - anchorCenterY);
    }

    parsePct(value, defaultPct) {
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) return defaultPct;
      return this.clamp(numericValue, 0, 100);
    }

    clamp(value, minValue, maxValue) {
      return Math.max(minValue, Math.min(maxValue, value));
    }

    resetItemInlineStyles(item) {
      item.style.removeProperty('--td-product-breakdown-item-line-width');
      item.style.removeProperty('--td-product-breakdown-item-line-height');
      item.style.removeProperty('--td-product-breakdown-item-shift');
    }

    clearDesktopStyles(items) {
      items.forEach((item) => {
        this.resetItemInlineStyles(item);
      });
    }

    teardown() {
      this.controller?.abort();
      this.controller = null;

      this.removeMqListener?.();
      this.removeMqListener = null;

      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }

      this.desktopMq = null;
      this.itemsContainer = null;
      this.media = null;
    }
  }

  customElements.define('td-product-breakdown', TdProductBreakdown);
}
